<div class="page-wrapper">
    <div class="card">

        <div class="card-header">
            <mat-icon class="warning-icon">link_off</mat-icon>
            <h1>Broken Link</h1>
            <p class="subtitle">This address doesn't match any page in the application.</p>
        </div>

        <div class="url-block">
            <span class="url-label">URL attempted</span>
            <code>{{ badUrl }}</code>
        </div>

        <!-- Sheila easter egg -->
        @if (isAidan) {
            <div class="advice-box aidan">
                <mat-icon>waving_hand</mat-icon>
                <div>
                    <strong>Sheila, is that you?! 👋</strong>
                    <p>
                        Have you got a dodgey bookmark? Or is it a shortcut from your laptop that generates these errors?
                        Please click the button below
                    </p>
                    <p>
                        Could you do us a huge favour and drop an email to
                        <a href="mailto:support@ouromc.ie?subject=Hi%20from%20Sheila%20-%20the%20broken%20link&body=Hi%20Greg%2C%0A%0ASo%20I%27ve%20been%20getting%20this%20broken%20link%20page%20and%20here%27s%20how%20it%20happens...">support@ouromc.ie</a>
                        and just let us know what you were doing when this pops up? We'd love to fix it properly.
                        A screenshot of this page would be brilliant too, especially the "Navigation history" section below!
                    </p>
                </div>
            </div>
        }

        <!-- PWA advice -->
        @if (likelyCause === 'pwa') {
            <div class="advice-box pwa">
                <mat-icon>install_mobile</mat-icon>
                <div>
                    <strong>You appear to be using OurOMC as an installed app.</strong>
                    <p>
                        You may have previously added OurOMC to your home screen. When an installed
                        app opens, your browser sometimes restores the last page you were on — and
                        in this case, that page no longer works as a direct link.
                    </p>
                    <p>
                        <strong>To fix this:</strong> Remove the OurOMC icon from your home screen,
                        open <code>manage.ouromc.ie</code> in your browser directly, and if you'd
                        like to add it to your home screen again, do so from the home page.
                    </p>
                    <p>
                        If this keeps happening, please take a screenshot of this page and email it to
                        <a href="mailto:support@ouromc.ie">support@ouromc.ie</a>.
                    </p>
                </div>
            </div>
        }

        <!-- Browser bookmark -->
        @if (likelyCause === 'bookmark') {
            <div class="advice-box bookmark">
                <mat-icon>bookmark</mat-icon>
                <div>
                    <strong>This looks like a saved browser bookmark.</strong>
                    <p>
                        The link includes a tab reference (<code>?topTab=…</code>) that is only
                        valid during an active session. Bookmarks saved from inside the app often
                        stop working when your session ends or when the app is updated.
                    </p>
                    <p>
                        <strong>To fix this:</strong> Remove this bookmark from your browser and
                        navigate to the page fresh after logging in. If the problem persists,
                        please take a screenshot of this page and email it to
                        <a href="mailto:support@ouromc.ie">support@ouromc.ie</a>.
                    </p>
                </div>
            </div>
        }

        <!-- Email / in-app link -->
        @if (likelyCause === 'email') {
            <div class="advice-box email">
                <mat-icon>email</mat-icon>
                <div>
                    <strong>This looks like a link from an email or notification.</strong>
                    <p>
                        The URL matches the pattern of an in-app page, but it appears to be
                        incomplete or out of date — possibly generated before a change was made
                        to the app, or from a notification that was sent with a malformed address.
                    </p>
                    <p>
                        <strong>Action:</strong> Please take a screenshot of this page —
                        including the "Navigation history" section below — and email it to
                        <a href="mailto:support@ouromc.ie">support@ouromc.ie</a> so we can
                        trace where the broken link came from and fix it.
                    </p>
                </div>
            </div>
        }

        <!-- Unknown -->
        @if (likelyCause === 'unknown') {
            <div class="advice-box unknown">
                <mat-icon>help_outline</mat-icon>
                <div>
                    <strong>We're not sure how you arrived here.</strong>
                    <p>
                        If you typed this address manually, please check it for typos and make
                        sure you're logged in first. If the app or an email brought you here,
                        please take a screenshot of this page and email it to
                        <a href="mailto:support@ouromc.ie">support@ouromc.ie</a>.
                    </p>
                </div>
            </div>
        }

        <!-- Diagnostic info -->
        <div class="diagnostic-section">
            <h2>
                <mat-icon>history</mat-icon>
                Navigation history
            </h2>

            <div class="diagnostic-box">
                <div class="diag-row">
                    <span class="diag-label">Time</span>
                    <span>{{ timestamp }}</span>
                </div>
                <div class="diag-row">
                    <span class="diag-label">URL</span>
                    <code class="diag-url">{{ badUrl }}</code>
                </div>
                <div class="diag-row">
                    <span class="diag-label">App mode</span>
                    <span>{{ isPwa ? 'Installed app (PWA)' : 'Browser tab' }}</span>
                </div>
                @if (events.length > 0) {
                    <div class="diag-row diag-events">
                        <span class="diag-label">Events</span>
                        <ul>
                            @for (event of events; track $index) {
                                <li>{{ event }}</li>
                            }
                        </ul>
                    </div>
                } @else {
                    <div class="diag-row">
                        <span class="diag-label">Events</span>
                        <span class="diag-none">No prior navigation — this was the first page loaded.</span>
                    </div>
                }
            </div>
        </div>

        <!-- Actions -->
        <div class="actions">
            <button mat-raised-button color="primary" routerLink="/">
                <mat-icon>home</mat-icon>
                Go to Home Page
            </button>

            <button mat-stroked-button (click)="copyDiagnostic()">
                <mat-icon>{{ copied ? 'check' : 'content_copy' }}</mat-icon>
                {{ copied ? 'Copied!' : 'Copy diagnostic info' }}
            </button>

            <a mat-stroked-button [attr.href]="emailHref">
                <mat-icon>mail</mat-icon>
                Email support
            </a>
        </div>

    </div>
</div>
